import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Flame, Star, Clock } from 'lucide-react';

const PromoCarousel = ({ promotions = [], onPromoClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Default promotions if none provided
  const defaultPromos = [
    {
      id: 1,
      title: "¡Hoy 2x1 en Cocteles!",
      subtitle: "Solo por tiempo limitado",
      image: "https://images.unsplash.com/photo-1611262359546-64e2822b2ab5?w=800&q=80",
      badge: "PROMOCIÓN",
      badgeColor: "bg-rose-500"
    },
    {
      id: 2,
      title: "Mariscos Frescos del Día",
      subtitle: "Directo del mar a tu mesa",
      image: "https://images.unsplash.com/photo-1579631542720-3a87824fff86?w=800&q=80",
      badge: "RECOMENDADO",
      badgeColor: "bg-amber-500"
    },
    {
      id: 3,
      title: "Combo Familiar $599",
      subtitle: "Ceviche + Tacos + Bebidas",
      image: "https://images.unsplash.com/photo-1534080564583-6be75777b70a?w=800&q=80",
      badge: "MÁS VENDIDO",
      badgeColor: "bg-emerald-500"
    }
  ];

  const slides = promotions.length > 0 ? promotions : defaultPromos;

  useEffect(() => {
    if (!isAutoPlaying || slides.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  if (slides.length === 0) return null;

  return (
    <div 
      className="relative w-full h-48 sm:h-56 md:h-64 rounded-2xl overflow-hidden mb-6 group"
      data-testid="promo-carousel"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Slides */}
      <div 
        className="flex transition-transform duration-500 ease-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div
            key={slide.id || index}
            className="min-w-full h-full relative cursor-pointer"
            onClick={() => onPromoClick?.(slide)}
            data-testid={`promo-slide-${index}`}
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-transparent" />
            
            {/* Content */}
            <div className="absolute inset-0 flex flex-col justify-center p-6 sm:p-8">
              {/* Badge */}
              {slide.badge && (
                <span className={`${slide.badgeColor || 'bg-cyan-500'} text-white text-xs font-bold px-3 py-1 rounded-full w-fit mb-3 flex items-center gap-1.5 animate-pulse`}>
                  {slide.badge === 'PROMOCIÓN' && <Flame className="w-3 h-3" />}
                  {slide.badge === 'RECOMENDADO' && <Star className="w-3 h-3" />}
                  {slide.badge === 'MÁS VENDIDO' && <Star className="w-3 h-3" />}
                  {slide.badge}
                </span>
              )}
              
              {/* Title */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 drop-shadow-lg">
                {slide.title}
              </h2>
              
              {/* Subtitle */}
              <p className="text-sm sm:text-base text-slate-300 max-w-xs">
                {slide.subtitle}
              </p>

              {/* CTA Button */}
              {slide.ctaText && (
                <button className="mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-6 py-2 rounded-full font-bold text-sm w-fit transition-all active:scale-95 shadow-lg shadow-cyan-500/30">
                  {slide.ctaText}
                </button>
              )}
            </div>

            {/* Timer indicator */}
            {slide.endTime && (
              <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-white font-medium">Termina pronto</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all"
            data-testid="promo-prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all"
            data-testid="promo-next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-cyan-400 w-6' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              data-testid={`promo-dot-${index}`}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      {isAutoPlaying && slides.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <div 
            className="h-full bg-cyan-400 transition-all"
            style={{
              width: '100%',
              animation: 'progress 4s linear infinite'
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default PromoCarousel;
